import axiosInstance, {
  getStaffId,
  getUserDetails,
  isAnglerOnlyUser,
  isCompanyManagerUser,
  isFisherWideUser,
  isIndependentScientistUser,
  isSuperUserSession,
} from "../components/content/axiosConfig";
import { getScopedAccountId } from "./accountContext";

/** Ponds for filter dropdowns — respects company/super scoped accountId. */
export async function fetchPondsForCurrentUser(userDetails) {
  const user = userDetails || getUserDetails();
  const scopedId = getScopedAccountId();

  if (isCompanyManagerUser(user)) {
    const params = {};
    if (scopedId) params.accountId = scopedId;
    const response = await axiosInstance.get("api/company/ponds", { params });
    const body = response.data;
    return Array.isArray(body) ? body : body?.data ?? [];
  }

  if (isSuperUserSession(user) && scopedId) {
    const response = await axiosInstance.get(`api/Pond/GetAllPond/${scopedId}`);
    const body = response.data;
    return Array.isArray(body) ? body : body?.data ?? [];
  }

  const staffId = getStaffId(user);
  if (!staffId) return [];

  const sessionFisherID = user?.FisherID;
  if (isFisherWideUser(user) && sessionFisherID != null && sessionFisherID !== "") {
    const response = await axiosInstance.get(`api/Pond/GetAllPond/${sessionFisherID}`);
    const body = response.data;
    return Array.isArray(body) ? body : body?.data ?? [];
  }

  const response = await axiosInstance.get(`api/Pond/GetAllPondsByStaff/${staffId}`);
  return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
}

/** Owner name in parentheses for scientists, company, and super scoped browse. */
export function shouldShowPondOwnerInDropdown(userDetails) {
  const u = userDetails || getUserDetails();
  if (!u) return false;

  if (isCompanyManagerUser(u) || isSuperUserSession(u)) return true;
  if (isIndependentScientistUser(u)) return true;
  if (u.IsFisherOwner || u.IsAdmin) return false;
  if (isAnglerOnlyUser(u)) return false;

  const defaultRole = String(u.DefaultPondRole || u.defaultPondRole || "").toLowerCase();
  if (defaultRole === "staff" || defaultRole === "angler") return false;

  if (u.hasScientistPondAccess) return true;
  return defaultRole === "scientist";
}

export function mapPondsToSelectOptions(ponds, userDetails = getUserDetails()) {
  const showOwner = shouldShowPondOwnerInDropdown(userDetails);
  return (ponds || []).map((pond) => ({
    label:
      showOwner && pond.FisherName
        ? `${pond.Name} (${pond.FisherName})`
        : pond.Name,
    value: pond.id,
    fisherID: pond.FisherID,
  }));
}

/** Session fisher for org staff; pond row / scoped account for company & super. */
export function resolveScopedFisherID(userDetails, ...sources) {
  const scopedId = getScopedAccountId();
  if (scopedId != null) return scopedId;
  if (userDetails?.FisherID != null && userDetails.FisherID !== "") {
    return userDetails.FisherID;
  }
  // Unscoped company: fall back to free Demo sandbox fisher.
  if (
    isCompanyManagerUser(userDetails) &&
    userDetails?.sandboxFisherId != null &&
    userDetails.sandboxFisherId !== ""
  ) {
    return Number(userDetails.sandboxFisherId);
  }
  for (const src of sources) {
    if (src?.FisherID != null && src.FisherID !== "") return src.FisherID;
    if (src?.fisherID != null && src.fisherID !== "") return src.fisherID;
  }
  return null;
}

export function optionalFisherQueryParam(fisherId) {
  if (fisherId == null || fisherId === "") return {};
  return { FisherID: fisherId };
}
